import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import LockOutlined from "@mui/icons-material/LockOutlined";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import type { TFunction } from "i18next";
import { type FC, type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChatMessageList } from "../../../components/UIToolkit/Chat/ChatMessageList";
import { ChatThinkingIndicator } from "../../../components/UIToolkit/Chat/ChatThinkingIndicator";
import { AiPromptComposer } from "../../../components/UIToolkit/Inputs/AiPromptComposer";
import { InlineLevelPicker, type LevelOption } from "../../../components/UIToolkit/Inputs/InlineLevelPicker";
import { AccentIconTile } from "../../../components/UIToolkit/molecules/AccentIconTile";
import { InlineHint } from "../../../components/UIToolkit/molecules/InlineHint";
import { FONT_SIZE, INK, LINE_HEIGHT, NEUTRAL } from "../../../theme/tokens";
import { ACCEPTED_EXAMPLE_EXTENSIONS, PRINTOUT_AI_PROMPT_MAX_LENGTH } from "../printoutAiSettings";
import { CustomPrintoutRequestBubble } from "./CustomPrintoutRequestBubble";
import { CustomPrintoutTurn } from "./CustomPrintoutTurn";
import { COMPOSER_MAX_ROWS, COMPOSER_MAX_ROWS_MEDIUM, COMPOSER_MAX_ROWS_NARROW } from "./consts";
import type { ChatEntry, DraftPhase, PendingRequest } from "./customPrintoutDraftState";
import { CUSTOM_PRINTOUT_EFFORTS, type CustomPrintoutEffort } from "./customPrintoutTypes";

type Props = {
	entries: ChatEntry[];
	pending: PendingRequest | null;
	phase: DraftPhase;
	prompt: string;
	onPromptChange: (prompt: string) => void;
	file: File | null;
	onFileChange: (file: File | null) => void;
	/** How much Annie thinks about the next request. */
	effort: CustomPrintoutEffort;
	onEffortChange: (effort: CustomPrintoutEffort) => void;
	onSubmit: () => void;
	/** The day is still loading: there is nothing to design against yet. */
	disabled: boolean;
	/** Bumped to put focus in the composer (Fix with Annie). */
	composerFocusKey: number;
	/** Save / Discard, between the conversation and the composer. */
	draftBar: ReactNode;
	/**
	 * An earlier version is on screen: the chat is that version's conversation, read-only — no composer, a note saying
	 * how to design from it instead. `null` while the current version (or a new printout) is shown.
	 */
	viewingVersionNumber: number | null;
	/** A new printout that starts from this saved printout: the intro says so. `null` otherwise. */
	startedFromName: string | null;
	/** The answer to Annie's "make it a summary?". */
	onAnswerSummaryQuestion: (accepted: boolean) => void;
};

const introOf = (t: TFunction, isReadOnly: boolean, startedFromName: string | null): string => {
	if (isReadOnly) return t("printout.custom.ai.version.chat.empty");
	return startedFromName === null ? t("printout.custom.ai.chat.intro") : t("printout.custom.ai.chat.intro.from", { name: startedFromName });
};

/**
 * "Design with Annie": the conversation so far — each request, who asked it, and Annie's one-line summary of what changed —
 * over the composer. Describing the sheet in words and attaching an example of it are the two ways in. While an earlier
 * version is on screen it shows how that version was designed, read-only.
 */
export const CustomPrintoutChat: FC<Props> = ({
	entries,
	pending,
	phase,
	prompt,
	onPromptChange,
	file,
	onFileChange,
	effort,
	onEffortChange,
	onSubmit,
	disabled,
	composerFocusKey,
	draftBar,
	viewingVersionNumber,
	startedFromName,
	onAnswerSummaryQuestion,
}) => {
	const { t } = useTranslation();
	const isGenerating = phase === "generating" || phase === "repairing";
	const isReadOnly = viewingVersionNumber !== null;
	const theme = useTheme();
	const isNarrow = useMediaQuery(theme.breakpoints.down("md"));
	const isMedium = useMediaQuery(theme.breakpoints.down("lg"));
	const composerMaxRows = isNarrow ? COMPOSER_MAX_ROWS_NARROW : isMedium ? COMPOSER_MAX_ROWS_MEDIUM : COMPOSER_MAX_ROWS;
	const effortOptions = useMemo<LevelOption<CustomPrintoutEffort>[]>(
		() =>
			CUSTOM_PRINTOUT_EFFORTS.map((level) => ({
				value: level,
				label: t(`printout.custom.ai.effort.${level}`),
				hint: t(`printout.custom.ai.effort.${level}.hint`),
			})),
		[t],
	);
	return (
		<Box
			data-testid="custom-printout-chat"
			sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 1.25, p: 2, border: `1px solid ${NEUTRAL[300]}`, borderRadius: 1 }}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
				<AccentIconTile size="xs">{isReadOnly ? <HistoryRounded /> : <AutoAwesomeIcon />}</AccentIconTile>
				<Typography component="h3" data-testid="custom-printout-chat-title" sx={{ color: "primary.dark", fontWeight: 600, fontSize: FONT_SIZE.EMPHASIS }}>
					{isReadOnly ? t("printout.custom.ai.version.chat.title", { number: viewingVersionNumber }) : t("printout.custom.ai.chat.title")}
				</Typography>
			</Box>

			{/* The gutter reaches into the card's padding, so an overlay scrollbar sits beside the right-aligned requests
			    rather than over their edge; the bubbles keep their width. */}
			<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", mx: -1, px: 1 }}>
				{entries.length === 0 && pending === null ? (
					<Typography data-testid="custom-printout-chat-intro" sx={{ fontSize: FONT_SIZE.CAPTION, lineHeight: LINE_HEIGHT.SNUG, color: INK.MUTED }}>
						{introOf(t, isReadOnly, startedFromName)}
					</Typography>
				) : (
					<ChatMessageList scrollKey={`${entries.length}-${pending === null ? "" : pending.request}-${phase}`} dataTestId="custom-printout-turns">
						{entries.map((entry, index) => (
							// The chat only ever grows, so an entry's position is its identity.
							// biome-ignore lint/suspicious/noArrayIndexKey: append-only list
							<CustomPrintoutTurn key={index} entry={entry} index={index} isGenerating={isGenerating} onAnswerSummaryQuestion={onAnswerSummaryQuestion} />
						))}
						{pending !== null && (pending.request !== "" || pending.hasAttachment) && (
							<CustomPrintoutRequestBubble author={{ kind: "viewer" }} dataTestId="custom-printout-pending-request">
								{pending.request === "" ? t("printout.custom.ai.turn.attachment") : pending.request}
							</CustomPrintoutRequestBubble>
						)}
						{isGenerating && (
							<ChatThinkingIndicator
								dataTestId="custom-printout-thinking"
								text={t(phase === "repairing" ? "printout.custom.ai.repairing.short" : "printout.custom.ai.generating.short")}
							/>
						)}
					</ChatMessageList>
				)}
			</Box>

			{isReadOnly ? (
				<InlineHint tone="neutral" icon={<LockOutlined />} dataTestId="custom-printout-chat-read-only" sx={{ flexShrink: 0 }}>
					{t("printout.custom.ai.version.chat.read.only", { number: viewingVersionNumber })}
				</InlineHint>
			) : (
				<>
					{draftBar}
					<AiPromptComposer
						key={composerFocusKey}
						autoFocus={composerFocusKey > 0}
						dataTestId="custom-printout-composer"
						value={prompt}
						onChange={onPromptChange}
						placeholder={t("printout.custom.ai.placeholder")}
						hint={t("printout.custom.ai.hint")}
						submitLabel={t(entries.length === 0 ? "printout.custom.ai.submit.first" : "printout.custom.ai.submit")}
						onSubmit={onSubmit}
						isSubmitting={isGenerating}
						disabled={disabled}
						maxLength={PRINTOUT_AI_PROMPT_MAX_LENGTH}
						maxRows={composerMaxRows}
						actionStart={
							// The level is part of the request, so it is fixed while one runs: changing it then would read as
							// changing the design already under way.
							<InlineLevelPicker
								options={effortOptions}
								value={effort}
								onChange={onEffortChange}
								ariaLabel={t("printout.custom.ai.effort.label")}
								disabled={disabled || isGenerating}
								dataTestId="custom-printout-effort"
							/>
						}
						attachment={{
							accept: ACCEPTED_EXAMPLE_EXTENSIONS,
							file,
							onFileChange,
							attachLabel: t("printout.ai.attach"),
							removeLabel: t("printout.ai.attachment.remove"),
							invalidTypeLabel: t("printout.ai.attachment.invalid"),
						}}
						sx={{ flexShrink: 0 }}
					/>
				</>
			)}
		</Box>
	);
};
